import { inject, Injectable } from '@angular/core';
import { FileService, StorageFileList, StorageIconFile } from '@evc/platform';
import { catchError, map, Observable, throwError } from 'rxjs';

import { BackendError, DataEnum, FileItem } from '../models';
import { FileVersion } from '../models/file-version.type';
import { FileBrowserBaseService } from './file-browser-base.service';

@Injectable()
export class DriveFileBrowserService extends FileBrowserBaseService {
  private readonly _fileService: FileService = inject(FileService);

  /**
   * Set the root directory in state. It will appear in first item of breadcrumb with the "root" property to true.
   * @param {{ name: string, id: string }} root - definition of the root. The name is required, the id can be null or undefined
   * Keep the id null or undefined to use the root of "Drive"
   *
   * @returns {void}
   */
  public setRoot({ name, id }: { name: string, id?: string }): void {
    this._state.next({ ...this._state.value, root: { name, id: id ?? null, root: true } as FileItem });
  }

  /**
   * Load items for a specific folder or root.
   * It will toggle the loading during the call
   * @param {FileItem} [parent = null] - will load items from this folder. Keep it empty to load from root
   * @return {Observable<void>} nothing if succeeded, throw error if the call fails
   */
  public loadItems(parent: FileItem | null = null): Observable<void> {
    this._state.next({ ...this._state.value, loading: true });

    const folderParent: { id: string | undefined } = (parent ?? this._state.value.root) || { id: undefined };

    return this._fileService.browse$({ parentId: folderParent.id, pageSize: 300 }).pipe(
      map((storageFileList: StorageFileList) => {
        this.loadItemsSuccess(this.toFileItems(storageFileList.items));
      }),
      catchError((error: BackendError | string) => {
        this.loadItemsFail();

        return throwError(() => error);
      }),
    );
  }

  private toFileItems(files: StorageIconFile[]): FileItem[] {
    return files.map((file: StorageIconFile) => ({
      id: file.id,
      name: file.name,
      type: DataEnum.FILE,
      tags: {},
      latestVersion: {
        id: file.latestVersion?.id ?? '',
        fileItemId: file.id,
        contentHash: file.latestVersion?.contentHash ?? '',
        contentType: file.latestVersion?.contentType ?? '',
        sizeInBytes: file.latestVersion?.sizeInBytes ?? 0,
      } satisfies FileVersion,
    }));
  }
}
